import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { estimatePoints } from '../api/loyalty.api';
import BulkInquiryModal from './BulkInquiryModal';
import { canonicalUrl, resolveAssetUrl, WHATSAPP_PHONE } from '../config';
import { shouldShowIncVatLabel } from '../utils/pricing';

function buildWhatsAppOrderMessage(product, displayPrice, variantSlug) {
    const slug = variantSlug || product.slug;
    const productUrl = canonicalUrl(`/product/${slug}`);
    return `Hello, I'd like to buy this product for delivery or pick up

*${product.name}*
*Price:* KShs ${Number(displayPrice || 0).toLocaleString()}
*URL:* ${productUrl}

Thank you!`;
}

const PHONE_TABLET_SLUGS = new Set([
    'phones-tablets',
    'smartphones',
    'smartphone',
    'feature-phones',
    'feature-phone',
    'tablets',
    'tablet',
    'mobiles',
    'mobile',
    'mobile-phones',
]);

/** Phones / tablets only — accessories, TVs, etc. keep the classic attr pickers. */
function isPhoneOrTabletProduct(product, attributeOptions = {}) {
    if (!product || product.isAccessory) return false;

    const cats = [
        ...(Array.isArray(product.categories) ? product.categories : []),
        ...(Array.isArray(product.categoryIds) ? product.categoryIds : []),
    ];

    const isTvOrHomeEnt = cats.some((c) => {
        const s = `${c?.slug || ''} ${c?.name || ''}`.toLowerCase();
        return /television|\btvs?\b|home-entertainment|home entertainment/.test(s);
    });
    if (isTvOrHomeEnt) return false;

    for (const c of cats) {
        const slug = String(c?.slug || '').toLowerCase().trim();
        const name = String(c?.name || '').toLowerCase().trim();
        if (/headphone|earphone|earbud|speaker|charger|case|cover|cable|accessory|power-bank/.test(slug)) {
            continue;
        }
        if (PHONE_TABLET_SLUGS.has(slug)) return true;
        if (slug.includes('phone') || slug.includes('tablet') || slug.includes('mobile')) {
            // Avoid accessories mis-tagged under phones-* paths when possible
            if (!/accessories?|cases?|chargers?|cables?/.test(slug)) return true;
        }
        if (/\b(smartphones?|feature phones?|tablets?|mobile phones?)\b/.test(name)) return true;
    }

    // RAM + storage attrs are the usual phone/tablet SKU shape.
    const slugs = Object.keys(attributeOptions || {});
    const hasRam = slugs.some(
        (s) => /^(ram|memory)$/i.test(s) || /(?:^|-)ram(?:-|$)/i.test(s)
    );
    const hasStorage = slugs.some((s) => /storage|rom|capacity|internal/i.test(s));
    if (hasRam && hasStorage) return true;

    return false;
}

function sameIdLocal(a, b) {
    if (a == null || b == null) return false;
    return String(a?._id || a) === String(b?._id || b);
}

function attrSortScore(slug) {
    const s = String(slug || '').toLowerCase();
    if (/storage|rom|capacity|internal/.test(s)) return 0;
    if (/ram|memory/.test(s)) return 1;
    return 2;
}

function variantSpecLabel(variant) {
    const attrs = [...(variant?.variantAttributes || [])].filter(
        (a) => a?.valueId?.name
    );
    attrs.sort(
        (a, b) =>
            attrSortScore(a.attributeId?.slug) - attrSortScore(b.attributeId?.slug)
    );
    return attrs.map((a) => a.valueId.name).join(' + ');
}

function variantThumbUrl(variant) {
    const imgs = variant?.variantImage || [];
    if (!imgs.length) return '';
    const primary = imgs.find((i) => i.isPrimary) || imgs[0];
    return primary?.path ? resolveAssetUrl(primary.path) : '';
}

function variantPricing(variant) {
    const salePrice = variant?.price?.sale_price || 0;
    const costPrice = variant?.price?.cost_price || 0;
    const offerPricing = variant?.offerPricing;
    const displayPrice = variant?.displayPrice ?? salePrice;
    const hasOffer = offerPricing?.offerPrice != null;
    const hasCatalogDiscount = !hasOffer && costPrice > salePrice && salePrice > 0;
    const compareAt = costPrice > displayPrice ? costPrice : 0;
    const discountPct =
        compareAt > 0
            ? Math.round(((compareAt - displayPrice) / compareAt) * 100)
            : 0;
    return { displayPrice, compareAt, discountPct, hasOffer, hasCatalogDiscount };
}

function isVariantUnpurchasable(variant) {
    if (!variant) return true;
    if (typeof variant.purchasable === 'boolean') return !variant.purchasable;
    if (typeof variant.inStock === 'boolean') return !variant.inStock;
    return Boolean(variant.forceOutOfStock);
}

function variantStockLeft(variant) {
    const stock =
        variant?.effectiveStock === null
            ? null
            : variant?.effectiveStock ?? variant?.stockCount;
    if (stock == null || stock <= 0) return null;
    return stock;
}

export default function ProductInfo({
    product,
    variants,
    selectedVariant,
    uniqueColors,
    attributeOptions,
    selectedColorId,
    selectedAttrs,
    isAttrValueCompatible,
    onColorChange,
    onAttrChange,
    onVariantSelect,
    quantity,
    onQuantityChange,
    onAddToCart,
    onBuyNow,
    onToggleWishlist,
    addingToCart,
    isInWishlist,
    isAuthenticated,
}) {
    const [bulkModalOpen, setBulkModalOpen] = useState(false);

    const bulkPrefill = selectedVariant ? [{
        productId: product._id,
        variantId: selectedVariant._id,
        productName: product.name,
        sku: selectedVariant.sku,
        imageUrl: selectedVariant.variantImage?.find((i) => i.isPrimary)?.path || selectedVariant.variantImage?.[0]?.path || '',
        quantity,
        unitPrice: selectedVariant?.price?.sale_price || 0,
    }] : [];

    const brandName = product.brandId?.name || product.brand?.name || '';
    const usePhoneTabletUi = isPhoneOrTabletProduct(product, attributeOptions);
    const salePrice = selectedVariant?.price?.sale_price || 0;
    const costPrice = selectedVariant?.price?.cost_price || 0;
    const offerPricing = selectedVariant?.offerPricing;
    const displayPrice = selectedVariant?.displayPrice ?? salePrice;
    const hasOffer = offerPricing?.offerPrice != null;
    const hasCatalogDiscount = !hasOffer && costPrice > salePrice && salePrice > 0;
    const discountPct = hasCatalogDiscount ? Math.round((costPrice - salePrice) / costPrice * 100) : 0;
    const stockCount = selectedVariant?.stockCount || 0;
    const effectiveStock =
        selectedVariant?.effectiveStock === null
            ? null
            : selectedVariant?.effectiveStock ?? stockCount;
    const isOutOfStock = (() => {
        if (!selectedVariant) return true;
        if (typeof selectedVariant.purchasable === "boolean") {
            return !selectedVariant.purchasable;
        }
        if (typeof selectedVariant.inStock === "boolean") {
            return !selectedVariant.inStock;
        }
        // Manual OOS flag only — warehouse stockCount is not storefront gate
        // when STOREFRONT_ENFORCE_STOCK=false.
        return Boolean(selectedVariant.forceOutOfStock);
    })();
    const maxQuantity = effectiveStock === null ? 10 : Math.max(effectiveStock || 0, 1);
    const showWhatsAppOrder = Boolean(product.orderViaWhatsApp);

    // Color thumbnails + one card per real SKU (attrs bundled) for phones/tablets.
    const colorThumbById = useMemo(() => {
        const map = new Map();
        for (const color of uniqueColors || []) {
            const match =
                (variants || []).find(
                    (v) =>
                        sameIdLocal(v.colorId, color._id) &&
                        !isVariantUnpurchasable(v) &&
                        variantThumbUrl(v)
                ) ||
                (variants || []).find(
                    (v) => sameIdLocal(v.colorId, color._id) && variantThumbUrl(v)
                );
            if (match) map.set(String(color._id), variantThumbUrl(match));
        }
        return map;
    }, [uniqueColors, variants]);

    // One card per RAM+storage (etc.) combo across the product. Prefer the
    // variant that matches the selected color so price/stock match what you see.
    const phoneSkuCards = useMemo(() => {
        if (!usePhoneTabletUi) return [];
        const byKey = new Map();

        const rank = (v) => {
            const colorMatch = selectedColorId
                ? sameIdLocal(v.colorId, selectedColorId)
                : true;
            const buyable = !isVariantUnpurchasable(v);
            return (colorMatch ? 4 : 0) + (buyable ? 2 : 0);
        };

        for (const v of variants || []) {
            const label = variantSpecLabel(v);
            if (!label) continue; // need real attrs (RAM/storage), not SKU fallback
            const key = label.toLowerCase();
            const prev = byKey.get(key);
            if (!prev || rank(v) > rank(prev.variant)) {
                byKey.set(key, { variant: v, label });
            }
        }

        return [...byKey.values()].sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true })
        );
    }, [usePhoneTabletUi, variants, selectedColorId]);

    const hasAttrPickers = useMemo(
        () =>
            Object.values(attributeOptions || {}).some(
                (attr) => (attr?.values || []).length >= 1
            ),
        [attributeOptions]
    );

    const selectedSpecLabel = selectedVariant
        ? variantSpecLabel(selectedVariant) || selectedVariant.sku || ''
        : '';

    const whatsappOrderUrl = useMemo(() => {
        if (!showWhatsAppOrder || !product?.slug) return '';
        const message = buildWhatsAppOrderMessage(
            product,
            displayPrice,
            selectedVariant?.slug
        );
        return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
    }, [showWhatsAppOrder, product, displayPrice, selectedVariant?.slug]);

    const handleOrderViaWhatsApp = () => {
        if (!whatsappOrderUrl) return;
        window.open(whatsappOrderUrl, '_blank', 'noopener,noreferrer');
    };

    // Helper to check if a variant with specific selections is out of stock
    const sameId = (a, b) => {
        if (a == null || b == null) return false;
        return String(a?._id || a) === String(b?._id || b);
    };

    const isUnpurchasable = (variant) => {
        if (!variant) return true;
        if (typeof variant.purchasable === "boolean") return !variant.purchasable;
        if (typeof variant.inStock === "boolean") return !variant.inStock;
        return Boolean(variant.forceOutOfStock);
    };

    /** Real SKUs that include this attr value (and other current attrs when compatible). */
    const variantsForAttrValue = (attrSlug, valueId) => {
        const trial = { ...selectedAttrs, [attrSlug]: String(valueId) };
        const exact = variants.filter((v) => {
            for (const [slug, vid] of Object.entries(trial)) {
                if (!vid) continue;
                const ok = (v.variantAttributes || []).some(
                    (a) => a.attributeId?.slug === slug && sameId(a.valueId, vid)
                );
                if (!ok) return false;
            }
            return true;
        });
        if (exact.length) return exact;
        return variants.filter((v) =>
            (v.variantAttributes || []).some(
                (a) => a.attributeId?.slug === attrSlug && sameId(a.valueId, valueId)
            )
        );
    };

    const isAttrValueOutOfStock = (attrSlug, valueId) => {
        const compatible =
            typeof isAttrValueCompatible === "function"
                ? isAttrValueCompatible(attrSlug, valueId)
                : true;
        // Fake / incompatible combos are disabled — never labeled Out of Stock
        if (!compatible) return false;
        const list = variantsForAttrValue(attrSlug, valueId);
        if (!list.length) return false;
        return list.every(isUnpurchasable);
    };

    const isColorUnavailable = (colorId) => {
        const colorVariants = variants.filter((v) => sameId(v.colorId, colorId));
        if (!colorVariants.length) return true;
        return colorVariants.every(isUnpurchasable);
    };

    return (
        <div>
            {/* Brand */}
            {brandName && (
                <div className="text-sm text-muted-foreground mb-2">{brandName}</div>
            )}

            {/* Product Name */}
            <h1 className="text-xl md:text-[1.65rem] font-bold text-foreground mb-3">{product.name}</h1>

            {/* Price */}
            <div className="mb-5">
                <div className="flex items-baseline gap-3 mb-1 flex-wrap">
                    <span className="text-2xl md:text-3xl font-bold text-primary">
                        KSh {displayPrice.toLocaleString()}
                    </span>
                    {hasOffer && costPrice > displayPrice && (
                        <span className="text-lg text-muted-foreground line-through">
                            KSh {costPrice.toLocaleString()}
                        </span>
                    )}
                    {!hasOffer && hasCatalogDiscount && (
                        <>
                            <span className="text-lg text-muted-foreground line-through">
                                KSh {costPrice.toLocaleString()}
                            </span>
                            <span className="px-3 py-1 bg-primary text-white text-sm font-bold rounded-md whitespace-nowrap">
                                {discountPct}% OFF
                            </span>
                        </>
                    )}
                    {hasOffer && offerPricing?.offer?.name && (
                        <span className="px-3 py-1 bg-primary text-white text-sm font-bold rounded-md whitespace-nowrap">
                            {offerPricing.offer.name}
                        </span>
                    )}
                </div>
                {shouldShowIncVatLabel(product?.vatMode) && (
                    <p className="text-xs text-muted-foreground mt-1">Inc. V.A.T</p>
                )}
            </div>

            {/* Loyalty Points Earning Badge */}
            <LoyaltyPointsBadge price={displayPrice} />

            {usePhoneTabletUi ? (
                <>
                    {/* Color as product thumbnails — show even when only one color */}
                    {uniqueColors.length > 0 && (
                        <div className="mb-5">
                            <div className="mb-2.5">
                                <span className="text-sm font-bold text-foreground">
                                    Selected Color:{' '}
                                    <span className="font-semibold text-muted-foreground">
                                        {uniqueColors.find(
                                            (c) => String(c._id) === String(selectedColorId || '')
                                        )?.name || ''}
                                    </span>
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                                {uniqueColors.map((color) => {
                                    const colorOutOfStock = isColorUnavailable(color._id);
                                    const selected =
                                        String(selectedColorId) === String(color._id);
                                    const thumb = colorThumbById.get(String(color._id));
                                    return (
                                        <button
                                            key={color._id}
                                            type="button"
                                            onClick={() => onColorChange(color._id)}
                                            className={`relative w-[4.5rem] h-[4.5rem] rounded-md overflow-hidden border-2 bg-muted/30 transition-all ${
                                                selected
                                                    ? 'border-foreground'
                                                    : colorOutOfStock
                                                      ? 'border-border opacity-55 hover:opacity-75'
                                                      : 'border-border hover:border-foreground/40'
                                            }`}
                                            title={`${color.name}${colorOutOfStock ? ' (Out of Stock)' : ''}`}
                                        >
                                            {thumb ? (
                                                <img
                                                    src={thumb}
                                                    alt={color.name}
                                                    className="w-full h-full object-contain p-0.5"
                                                />
                                            ) : (
                                                <div
                                                    className="w-full h-full"
                                                    style={{
                                                        backgroundColor:
                                                            color.hexCode || '#e5e7eb',
                                                    }}
                                                />
                                            )}
                                            {colorOutOfStock && (
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-white/35">
                                                    <div className="absolute w-[120%] h-0.5 bg-red-500 rotate-45" />
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Combined SKU cards (storage + RAM, etc.) — show even a single card */}
                    {phoneSkuCards.length > 0 ? (
                        <div className="mb-5">
                            <div className="mb-2.5">
                                <span className="text-sm font-bold text-foreground">
                                    Variant:{' '}
                                    <span className="font-semibold text-muted-foreground">
                                        {selectedSpecLabel || phoneSkuCards[0]?.label || ''}
                                    </span>
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                                {phoneSkuCards.map(({ variant: v, label }) => {
                                    const stockVariant =
                                        (selectedColorId &&
                                            ((variants || []).find(
                                                (x) =>
                                                    sameIdLocal(x.colorId, selectedColorId) &&
                                                    variantSpecLabel(x) === label &&
                                                    !isVariantUnpurchasable(x)
                                            ) ||
                                                (variants || []).find(
                                                    (x) =>
                                                        sameIdLocal(x.colorId, selectedColorId) &&
                                                        variantSpecLabel(x) === label
                                                ))) ||
                                        v;
                                    const selected =
                                        sameIdLocal(stockVariant._id, selectedVariant?._id) ||
                                        variantSpecLabel(selectedVariant) === label;
                                    const pricing = variantPricing(stockVariant);
                                    const stockLeft = variantStockLeft(stockVariant);
                                    const showOos = isVariantUnpurchasable(stockVariant);

                                    return (
                                        <button
                                            key={label}
                                            type="button"
                                            onClick={() => {
                                                if (typeof onVariantSelect === 'function') {
                                                    onVariantSelect(stockVariant);
                                                }
                                            }}
                                            className={`min-w-[9.5rem] max-w-[11rem] flex-1 text-left rounded-lg border-2 px-3 py-2.5 transition-all ${
                                                selected
                                                    ? 'border-foreground bg-card'
                                                    : 'border-border bg-card hover:border-foreground/35'
                                            }`}
                                        >
                                            <div
                                                className={`text-sm font-bold mb-1 ${
                                                    selected
                                                        ? 'text-foreground'
                                                        : 'text-muted-foreground'
                                                }`}
                                            >
                                                {label}
                                            </div>
                                            {showOos ? (
                                                <div className="text-sm font-medium text-orange-600">
                                                    Out of stock
                                                </div>
                                            ) : (
                                                <>
                                                    {pricing.discountPct > 0 && (
                                                        <div className="flex items-center gap-1.5 text-xs mb-0.5">
                                                            <span className="inline-flex items-center gap-0.5 text-emerald-600 font-semibold">
                                                                <i className="ri-arrow-down-s-fill text-base leading-none" />
                                                                {pricing.discountPct}%
                                                            </span>
                                                            {pricing.compareAt > 0 && (
                                                                <span className="text-muted-foreground line-through">
                                                                    {pricing.compareAt.toLocaleString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="text-base font-bold text-foreground">
                                                        KSh {pricing.displayPrice.toLocaleString()}
                                                    </div>
                                                    {stockLeft != null && stockLeft <= 5 && (
                                                        <div className="text-xs font-medium text-orange-600 mt-0.5">
                                                            {stockLeft} left
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : hasAttrPickers ? (
                        /* Fallback: show Storage / RAM pickers when cards couldn't be built */
                        Object.entries(attributeOptions).map(([attrSlug, attr]) => {
                            if (!attr.values?.length) return null;
                            return (
                                <div key={attrSlug} className="mb-5">
                                    <div className="flex items-center justify-between mb-2.5">
                                        <span className="text-sm font-bold text-foreground">{attr.name}:</span>
                                        <span className="text-sm text-muted-foreground">
                                            {attr.values.find((v) => String(v._id) === String(selectedAttrs[attrSlug] || ""))?.name ||
                                                attr.values[0]?.name ||
                                                ''}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2.5">
                                        {attr.values.map((val) => {
                                            const isSelected =
                                                String(selectedAttrs[attrSlug] || "") === String(val._id) ||
                                                (!selectedAttrs[attrSlug] &&
                                                    String(attr.values[0]?._id) === String(val._id));
                                            const attrOutOfStock = isAttrValueOutOfStock(attrSlug, val._id);
                                            return (
                                                <button
                                                    key={val._id}
                                                    type="button"
                                                    onClick={() => onAttrChange(attrSlug, val._id)}
                                                    className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all whitespace-nowrap ${
                                                        isSelected
                                                            ? attrOutOfStock
                                                                ? "border-red-500 bg-red-50 text-red-600"
                                                                : "border-foreground bg-card text-foreground"
                                                            : attrOutOfStock
                                                              ? "border-border bg-muted/50 text-muted-foreground/60 opacity-60"
                                                              : "border-border bg-card text-foreground hover:border-foreground/40"
                                                    }`}
                                                >
                                                    {val.name}
                                                    {attrOutOfStock ? (
                                                        <span className="ml-1 text-xs text-orange-600">(Out of stock)</span>
                                                    ) : null}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    ) : null}
                </>
            ) : (
                <>
                    {/* Color Selection — show even when only one color */}
                    {uniqueColors.length > 0 && (
                        <div className="mb-5">
                            <div className="flex items-center justify-between mb-2.5">
                                <span className="text-sm font-bold text-foreground">Color:</span>
                                <span className="text-sm text-muted-foreground">
                                    {uniqueColors.find((c) => String(c._id) === String(selectedColorId || ""))?.name || ''}
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                                {uniqueColors.map((color) => {
                                    const colorOutOfStock = isColorUnavailable(color._id);
                                    return (
                                        <button
                                            key={color._id}
                                            onClick={() => onColorChange(color._id)}
                                            className={`relative w-10 h-10 rounded-full border-2 transition-all ${
                                                String(selectedColorId) === String(color._id)
                                                    ? 'border-primary scale-110'
                                                    : colorOutOfStock
                                                    ? 'border-border opacity-50 hover:opacity-70'
                                                    : 'border-border hover:border-border'
                                            }`}
                                            title={`${color.name}${colorOutOfStock ? ' (Out of Stock)' : ''}`}
                                        >
                                            <div
                                                className="w-full h-full rounded-full border border-black/10"
                                                style={{
                                                    backgroundColor:
                                                        color.hexCode ||
                                                        (color.name?.toLowerCase() === 'white' || color.name?.toLowerCase() === 'standard'
                                                            ? '#e5e7eb'
                                                            : '#9ca3af'),
                                                }}
                                            ></div>
                                            {String(selectedColorId) === String(color._id) && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <i className="ri-check-line text-white text-xl drop-shadow-lg"></i>
                                                </div>
                                            )}
                                            {colorOutOfStock && (
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                    <div className="absolute w-full h-0.5 bg-red-500 rotate-45"></div>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Dynamic Attribute Selectors */}
                    {Object.entries(attributeOptions).map(([attrSlug, attr]) => {
                        if (attr.values.length <= 1) return null;

                        return (
                            <div key={attrSlug} className="mb-5">
                                <div className="flex items-center justify-between mb-2.5">
                                    <span className="text-sm font-bold text-foreground">{attr.name}:</span>
                                    <span className="text-sm text-muted-foreground">
                                        {attr.values.find((v) => String(v._id) === String(selectedAttrs[attrSlug] || ""))?.name || ''}
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2.5">
                                    {attr.values.map((val) => {
                                        const compatible =
                                            typeof isAttrValueCompatible === "function"
                                                ? isAttrValueCompatible(attrSlug, val._id)
                                                : true;
                                        const attrOutOfStock = isAttrValueOutOfStock(attrSlug, val._id);
                                        const isSelected = String(selectedAttrs[attrSlug] || "") === String(val._id);
                                        // Incompatible with current selection → disabled (not OOS).
                                        // Still allow click so shoppers can jump to a real sibling SKU
                                        // (e.g. 1GB/16GB → click 32GB → 2GB/32GB).
                                        const isDisabledCombo = !compatible && !isSelected;

                                        return (
                                            <button
                                                key={val._id}
                                                type="button"
                                                onClick={() => onAttrChange(attrSlug, val._id)}
                                                aria-disabled={isDisabledCombo}
                                                title={
                                                    isDisabledCombo
                                                        ? "Not available with current selection — tap to switch to a matching option"
                                                        : attrOutOfStock
                                                          ? `${val.name} (Out of Stock)`
                                                          : val.name
                                                }
                                                className={`px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all whitespace-nowrap relative ${
                                                    isSelected
                                                        ? attrOutOfStock
                                                            ? "border-red-500 bg-red-50 text-red-600"
                                                            : "border-primary bg-primary text-white"
                                                        : isDisabledCombo
                                                          ? "border-border bg-muted/40 text-muted-foreground/45 opacity-45"
                                                          : attrOutOfStock
                                                            ? "border-border bg-muted/50 text-muted-foreground/60 opacity-60 hover:opacity-80"
                                                            : "border-border bg-card text-foreground hover:border-primary"
                                                }`}
                                            >
                                                <span className={attrOutOfStock && !isSelected ? "line-through" : ""}>
                                                    {val.name}
                                                </span>
                                                {attrOutOfStock && isSelected && (
                                                    <span className="ml-2 text-xs">(Out of Stock)</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </>
            )}

            {/* Stock — only show when out of stock */}
            {isOutOfStock && (
            <div className="mb-5">
                    <div className="flex items-center gap-2 text-red-600">
                        <i className="ri-close-circle-fill"></i>
                        <span className="text-sm font-medium">Out of Stock</span>
                    </div>
            </div>
            )}

            {/* Quantity + Order via WhatsApp */}
            <div className="mb-5">
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                    <div className="flex-1 min-w-0">
                        <span className="text-sm font-bold text-foreground mb-2.5 block">Quantity:</span>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                                disabled={isOutOfStock || quantity <= 1}
                                className="w-9 h-9 flex items-center justify-center border-2 border-border rounded-lg hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="ri-subtract-line"></i>
                            </button>
                            <span className="w-9 text-center font-semibold text-foreground text-base">{quantity}</span>
                            <button
                                onClick={() => onQuantityChange(Math.min(maxQuantity, quantity + 1))}
                                disabled={isOutOfStock || quantity >= maxQuantity}
                                className="w-9 h-9 flex items-center justify-center border-2 border-border rounded-lg hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <i className="ri-add-line"></i>
                            </button>
                        </div>
                    </div>

                    {showWhatsAppOrder && (
                        <button
                            type="button"
                            onClick={handleOrderViaWhatsApp}
                            disabled={isOutOfStock}
                            className="w-full sm:w-auto sm:min-w-[200px] py-2.5 px-5 rounded-lg font-semibold bg-[#25D366] text-white hover:brightness-110 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <i className="ri-whatsapp-line text-lg"></i>
                            Order via WhatsApp
                        </button>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-3">
                    <button
                        onClick={onAddToCart}
                        disabled={addingToCart || isOutOfStock}
                        className={`py-3 rounded-lg font-bold text-white transition-all whitespace-nowrap disabled:cursor-not-allowed ${
                            isOutOfStock
                                ? 'bg-muted-foreground/40 opacity-60'
                                : 'bg-primary hover:brightness-110 hover:shadow-lg disabled:opacity-60'
                        }`}
                    >
                        {addingToCart ? (
                            <><i className="ri-loader-4-line animate-spin mr-1"></i><span className="hidden sm:inline">Adding...</span><span className="sm:hidden">...</span></>
                        ) : isOutOfStock ? (
                            <><i className="ri-close-circle-line mr-1"></i>Out of Stock</>
                        ) : (
                            <><i className="ri-shopping-cart-line mr-1"></i>Add to Cart</>
                        )}
                    </button>
                    <button
                        onClick={onBuyNow}
                        disabled={addingToCart || isOutOfStock}
                        className={`py-3 rounded-lg font-bold border-2 transition-colors whitespace-nowrap disabled:cursor-not-allowed ${
                            isOutOfStock
                                ? 'border-border text-muted-foreground/60 bg-muted opacity-60'
                                : 'border-primary text-primary hover:bg-primary hover:text-white disabled:opacity-60'
                        }`}
                    >
                        <i className="ri-flashlight-line mr-1"></i>
                        Buy Now
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    {onToggleWishlist && (
                        <button
                            onClick={onToggleWishlist}
                            className={`py-2.5 rounded-lg font-medium border-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                                isInWishlist
                                    ? 'border-primary text-primary'
                                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                            }`}
                        >
                            <i className={`${isInWishlist ? 'ri-heart-fill' : 'ri-heart-line'} mr-1`}></i>
                            <span className="hidden sm:inline">{isInWishlist ? 'In Wishlist' : 'Add to Wishlist'}</span>
                            <span className="sm:hidden">Wishlist</span>
                        </button>
                    )}
                    <button
                        onClick={() => setBulkModalOpen(true)}
                        className={`py-2.5 rounded-lg font-medium border-2 border-primary text-primary hover:bg-primary hover:text-white transition-colors whitespace-nowrap text-sm sm:text-base ${!onToggleWishlist ? 'col-span-2' : ''}`}
                    >
                        <i className="ri-briefcase-line mr-1"></i>
                        <span className="hidden sm:inline">Bulk / Corporate Order</span>
                        <span className="sm:hidden">Bulk Order</span>
                    </button>
                </div>
            </div>

            <BulkInquiryModal
                isOpen={bulkModalOpen}
                onClose={() => setBulkModalOpen(false)}
                prefillItems={bulkPrefill}
                source="pdp"
            />

            {/* Delivery / return / warranty — Product Benefits master (with local fallback) */}
            {(
                Array.isArray(product?.benefits) && product.benefits.length > 0
                    ? product.benefits
                    : [
                        { title: 'Free Delivery', subtitle: 'On orders above KSh 5,000', icon: 'ri-truck-line' },
                        { title: '7 Days Return', subtitle: 'Easy return and exchange policy', icon: 'ri-arrow-left-right-line' },
                        { title: '1 Year Warranty', subtitle: 'Manufacturer warranty included', icon: 'ri-shield-check-line' },
                    ]
            ).map((item, index) => (
                <div
                    key={`${item.title}-${index}`}
                    className={`flex items-start gap-3 text-sm ${index === 0 ? 'mt-5' : ''} ${index > 0 ? 'mt-2.5' : ''}`}
                >
                    <i className={`${item.icon || 'ri-checkbox-circle-line'} text-success text-xl`}></i>
                    <div>
                        <div className="font-medium text-foreground">{item.title}</div>
                        {item.subtitle ? (
                            <div className="text-muted-foreground">{item.subtitle}</div>
                        ) : null}
                    </div>
                </div>
            ))}
        </div>
    );
}

function LoyaltyPointsBadge({ price }) {
    const [points, setPoints] = useState(0);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!price) return;
        const fetchEstimate = async () => {
            setLoading(true);
            try {
                const res = await estimatePoints(price);
                if (res?.data?.isOk) {
                    setPoints(res.data.data.points);
                }
            } catch (err) {
                console.error('Error estimating points:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchEstimate();
    }, [price]);

    if (loading || points <= 0) return null;

    return (
        <div className="flex flex-col gap-2 mb-5">
            <div className="inline-flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-1.5">
                <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center">
                    <i className="ri-coin-line text-primary"></i>
                </div>
                <div>
                    <p className="text-sm font-bold text-foreground">
                        Earn {points.toLocaleString()} Loyalty Points!
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                        Redeemable on future orders
                    </p>
                </div>
            </div>
            <Link to="/loyalty-program" className="text-[11px] text-primary hover:underline flex items-center gap-1 px-1">
                Learn how it works <i className="ri-arrow-right-s-line"></i>
            </Link>
        </div>
    );
}
